import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [colorIndex, setColorIndex] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Color palettes for dynamic circles
  const colorPalettes = [
    { c1: 'rgba(100, 180, 255, 0.3)', c2: 'rgba(0, 200, 220, 0.25)', c3: 'rgba(80, 140, 200, 0.3)', c4: 'rgba(0, 180, 216, 0.25)' },
    { c1: 'rgba(0, 180, 216, 0.35)', c2: 'rgba(100, 180, 255, 0.25)', c3: 'rgba(26, 81, 146, 0.3)', c4: 'rgba(80, 200, 230, 0.25)' },
    { c1: 'rgba(80, 200, 230, 0.3)', c2: 'rgba(13, 58, 110, 0.25)', c3: 'rgba(0, 180, 216, 0.35)', c4: 'rgba(100, 180, 255, 0.25)' },
  ];

  // Dynamic color change effect
  useEffect(() => {
    const interval = setInterval(() => {
      setColorIndex((prev) => (prev + 1) % colorPalettes.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const currentColors = colorPalettes[colorIndex];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Por favor complete todos los campos');
      return;
    }
    
    setIsLoading(true);
    
    const result = await login(username, password);
    
    if (result.success) {
      toast.success('Bienvenido a Oftalmodiagnosis');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0d3a6e 0%, #1a5192 35%, #00a8c6 100%)',
      }}
      data-testid="login-page"
    >
      {/* Animated Background Circles */}
      <div 
        className="absolute rounded-full transition-all duration-[4000ms] ease-in-out"
        style={{
          width: '550px',
          height: '550px',
          top: '-180px',
          left: '25%',
          backgroundColor: currentColors.c1,
        }}
      />
      <div 
        className="absolute rounded-full transition-all duration-[4000ms] ease-in-out"
        style={{
          width: '650px',
          height: '650px',
          top: '15%',
          right: '-250px',
          backgroundColor: currentColors.c2,
        }}
      />
      <div 
        className="absolute rounded-full transition-all duration-[4000ms] ease-in-out"
        style={{
          width: '450px',
          height: '450px',
          bottom: '-120px',
          right: '15%',
          backgroundColor: currentColors.c3,
        }}
      />
      <div 
        className="absolute rounded-full transition-all duration-[4000ms] ease-in-out"
        style={{
          width: '380px',
          height: '380px',
          bottom: '5%',
          left: '-120px',
          backgroundColor: currentColors.c4,
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-md px-6 animate-fade-in">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="w-[200px] h-[200px] mx-auto mb-6 bg-white rounded-full p-6 shadow-2xl">
            <img 
              src="/images/logo.png" 
              alt="Oftalmodiagnosis Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <p className="text-white/70 text-sm">
            Sistema de Gestión de Pacientes
          </p>
        </div>
        
        {/* Login Card */}
        <div 
          className="backdrop-blur-xl rounded-2xl p-8 shadow-2xl"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <h2 className="font-heading text-xl font-semibold text-white text-center mb-6">
            Iniciar Sesión
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white/80 text-sm font-medium">
                Usuario
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Ingresa tu usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 focus:ring-white/20"
                data-testid="login-username-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80 text-sm font-medium">
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Ingresa tu contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 focus:ring-white/20 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" strokeWidth={1.5} />
                  ) : (
                    <Eye className="w-5 h-5" strokeWidth={1.5} />
                  )}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-white hover:bg-white/90 text-brand-dark font-semibold shadow-lg transition-all active:scale-[0.98]"
              data-testid="login-submit-button"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-brand-dark/30 border-t-brand-dark rounded-full animate-spin"></div>
                  <span>Ingresando...</span>
                </div>
              ) : (
                <span>Iniciar Sesión</span>
              )}
            </Button>
          </form>
        </div>
        
        {/* Footer */}
        <p className="text-center text-white/40 text-xs mt-6">
          © {new Date().getFullYear()} Oftalmodiagnosis
        </p>
      </div>
    </div>
  );
};

export default Login;
